import { supabase } from './supabase'

/**
 * Get all active custom habit categories for an organization
 */
export const getCustomHabitCategories = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('custom_habit_categories')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .order('display_order', { ascending: true })
      .order('name', { ascending: true })

    if (error) throw error

    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error fetching custom habit categories:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Create a new custom habit category
 */
export const createCustomHabitCategory = async (organizationId, categoryData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    // Get the highest display_order for this organization
    const { data: existingCategories } = await supabase
      .from('custom_habit_categories')
      .select('display_order')
      .eq('organization_id', organizationId)
      .order('display_order', { ascending: false })
      .limit(1)

    const displayOrder = existingCategories && existingCategories.length > 0
      ? existingCategories[0].display_order + 1
      : 0

    const { data, error } = await supabase
      .from('custom_habit_categories')
      .insert({
        organization_id: organizationId,
        name: categoryData.name.trim(),
        description: categoryData.description?.trim() || null,
        icon: categoryData.icon || null,
        display_order: displayOrder,
        is_active: true,
        created_by: user.id,
      })
      .select()
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    console.error('Error creating custom habit category:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Update a custom habit category
 */
export const updateCustomHabitCategory = async (categoryId, updates) => {
  try {
    const updateData = {}
    if (updates.name !== undefined) updateData.name = updates.name.trim()
    if (updates.description !== undefined) updateData.description = updates.description?.trim() || null
    if (updates.icon !== undefined) updateData.icon = updates.icon || null
    if (updates.display_order !== undefined) updateData.display_order = updates.display_order
    if (updates.is_active !== undefined) updateData.is_active = updates.is_active

    const { data, error } = await supabase
      .from('custom_habit_categories')
      .update(updateData)
      .eq('id', categoryId)
      .select()
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    console.error('Error updating custom habit category:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Delete a custom habit category (soft delete by setting is_active to false)
 */
export const deleteCustomHabitCategory = async (categoryId) => {
  try {
    const { error } = await supabase
      .from('custom_habit_categories')
      .update({ is_active: false })
      .eq('id', categoryId)

    if (error) throw error

    return { success: true }
  } catch (error) {
    console.error('Error deleting custom habit category:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Reorder custom habit categories
 */
export const reorderCustomHabitCategories = async (organizationId, categoryIds) => {
  try {
    const updates = categoryIds.map((id, index) => ({
      id,
      display_order: index,
    }))

    const { error } = await supabase
      .from('custom_habit_categories')
      .upsert(updates.map(u => ({ id: u.id, display_order: u.display_order })), {
        onConflict: 'id',
      })

    if (error) throw error

    return { success: true }
  } catch (error) {
    console.error('Error reordering custom habit categories:', error)
    return { success: false, error: error.message }
  }
}

